import { useState } from 'react';

import HubRoundedIcon from '@mui/icons-material/HubRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { testAddressProviderConnection } from '../../api/addressSettingsApi';
import { useAddressSettings, useUpdateAddressSettings } from '../../hooks';
import { useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { AddressFormat, UpdateAddressSettingsRequest } from '../../model/settingsTypes';
import {
  ChipListField,
  FeedbackSnackbar,
  FieldRow,
  MaskedCredentialField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TestConnectionButton,
  TextInputField,
} from '../components';
import { ADDRESS_FORMAT_OPTIONS } from '../options';

/** Address settings: format, validation and the geocoding/suggestion provider. */
export function AddressSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage, canManageCredentials } = useDomainPermissions('address');
  const query = useAddressSettings();
  const update = useUpdateAddressSettings();
  const form = useSettingsForm(query.data);
  const [providerSecret, setProviderSecret] = useState('');
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const dirty = form.dirty || providerSecret.trim() !== '';

  const reset = () => {
    form.reset();
    setProviderSecret('');
  };

  const handleSave = () => {
    const draft = form.draft;
    if (!draft) {
      return;
    }
    const body: UpdateAddressSettingsRequest = {
      version: draft.version,
      supportedCountries: draft.supportedCountries,
      defaultCountry: draft.defaultCountry,
      addressFormat: draft.addressFormat,
      requiredAddressFields: draft.requiredAddressFields,
      validationEnabled: draft.validationEnabled,
      geocodingEnabled: draft.geocodingEnabled,
      suggestionsEnabled: draft.suggestionsEnabled,
      provider: {
        code: draft.provider?.code ?? null,
        baseUrl: draft.provider?.baseUrl ?? null,
        ...(providerSecret.trim() !== '' ? { secret: providerSecret } : {}),
      },
    };
    update.mutate(body, {
      onSuccess: () => {
        setProviderSecret('');
        success(t('addressSettings.savedFeedback'));
      },
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('addressSettings.title')}
      subtitle={t('addressSettings.subtitle')}
      icon={<PlaceRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;

        const setProvider = (patch: { code?: string | null; baseUrl?: string | null }) =>
          form.setField('provider', {
            code: draft.provider?.code ?? null,
            baseUrl: draft.provider?.baseUrl ?? null,
            credential: draft.provider?.credential ?? null,
            ...patch,
          });

        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('addressSettings.formatSection.title')} icon={<RuleRoundedIcon />}>
              <ChipListField
                label={t('addressSettings.formatSection.supportedCountriesLabel')}
                description={t('addressSettings.formatSection.supportedCountriesDescription')}
                value={draft.supportedCountries}
                onChange={(value) => form.setField('supportedCountries', value)}
                disabled={disabled}
              />
              <TextInputField
                label={t('addressSettings.formatSection.defaultCountryLabel')}
                value={draft.defaultCountry ?? ''}
                onChange={(value) => form.setField('defaultCountry', value)}
                disabled={disabled}
                placeholder="RU"
              />
              <SelectField
                label={t('addressSettings.formatSection.addressFormatLabel')}
                value={draft.addressFormat}
                options={ADDRESS_FORMAT_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) => form.setField('addressFormat', value as AddressFormat)}
                disabled={disabled}
              />
              <ChipListField
                label={t('addressSettings.formatSection.requiredFieldsLabel')}
                description={t('addressSettings.formatSection.requiredFieldsDescription')}
                value={draft.requiredAddressFields}
                onChange={(value) => form.setField('requiredAddressFields', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('addressSettings.formatSection.validationLabel')}
                description={t('addressSettings.formatSection.validationDescription')}
                checked={draft.validationEnabled}
                onChange={(value) => form.setField('validationEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('addressSettings.formatSection.geocodingLabel')}
                checked={draft.geocodingEnabled}
                onChange={(value) => form.setField('geocodingEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('addressSettings.formatSection.suggestionsLabel')}
                checked={draft.suggestionsEnabled}
                onChange={(value) => form.setField('suggestionsEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard
              title={t('addressSettings.providerSection.title')}
              description={t('addressSettings.providerSection.description')}
              icon={<HubRoundedIcon />}
            >
              <TextInputField
                label={t('addressSettings.providerSection.codeLabel')}
                value={draft.provider?.code ?? ''}
                onChange={(value) => setProvider({ code: value || null })}
                disabled={disabled}
                placeholder="dadata"
              />
              <TextInputField
                label={t('addressSettings.providerSection.baseUrlLabel')}
                type="url"
                value={draft.provider?.baseUrl ?? ''}
                onChange={(value) => setProvider({ baseUrl: value || null })}
                disabled={disabled}
                placeholder="https://suggestions.dadata.ru"
              />
              <MaskedCredentialField
                label={t('addressSettings.providerSection.credentialLabel')}
                description={t('addressSettings.providerSection.credentialDescription')}
                credential={draft.provider?.credential ?? null}
                secret={providerSecret}
                onSecretChange={setProviderSecret}
                disabled={!canManageCredentials}
              />
              <FieldRow
                label={t('addressSettings.providerSection.connectionLabel')}
                description={t('addressSettings.providerSection.connectionDescription')}
                alignTop
              >
                <TestConnectionButton run={testAddressProviderConnection} disabled={!canManage} />
              </FieldRow>
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
